// Left panel: tabbed sidebar with scene tree, history, and hotkeys tabs.
// Replaces the old floating history/shortcuts panels.

import type { EditorNavigationController } from './editorNavigation.js';
import type { NodeEditorController } from './nodeEditor.js';
import { subscribeEditorTargetSynced } from '../editor/editorEvents.js';
import type { AppState, EmptyInspectorPanel, RuntimeGraphKind, SceneFavoriteTarget, SvgPathShape } from '../state/appState.js';
import type { EditorCommands } from '../editor/commands.js';
import type { HistoryStore } from '../editor/historyState.js';
import type { EventRuntimeManager } from '../app/eventRuntime.js';
import type { DataRuntimeManager } from '../app/dataRuntime.js';
import type { DataSourceRuntimeManager } from '../app/dataSourceRuntime.js';
import type { QueryRuntimeManager } from '../app/queryRuntime.js';
import type { CommandBatch } from '../editor/commandHistory.js';
import { ROOT_DATA_RECORDS, type DataFieldDef, type DataGraphDef, type DataRecordsDef, type DataSourceDef } from '../graph/dataGraph.js';
import { dataFieldPathKey, dataFieldPathLabel, discoverDataFields, uniqueDataFieldId } from '../graph/dataSourceSchema.js';
import { resolveDataRecords } from '../app/templateLoader.js';
import type { EventGraphDef, RuntimeStateSchema } from '../graph/eventGraph.js';
import {
  getSceneFavoriteTargetLabel,
  isSceneFavoriteTargetValid,
  removeSceneFavorite,
} from '../state/sceneFavorites.js';
import { svgPathInfoWasm } from 'fluveria-engine-wasm';

interface LeftPanelDeps {
  appState: AppState;
  editorNavigation: Pick<EditorNavigationController, 'openLibraryEditor' | 'returnToScene' | 'render'>;
  historyStore: HistoryStore;
  nodeEditor: Pick<NodeEditorController, 'renderGraph'>;
  renderSceneTree: () => void;
  selectSceneFavorite: (target: SceneFavoriteTarget) => void;
  scheduleRender: () => void;
  updateParams: () => void;
  commandBatch: CommandBatch;
  commands: Pick<EditorCommands, 'setRuntimeGraph' | 'setDataOwner' | 'setDataSources' | 'setShapes' | 'setRuntimeModel' | 'setRuntimeState' | 'setSceneDataContext'>;
  loadRuntimeGraph: (kind: RuntimeGraphKind, id: string) => boolean;
  eventRuntime: EventRuntimeManager;
  dataRuntime: Pick<DataRuntimeManager, 'getDataSources' | 'getSourceDocument'>;
  dataSourceRuntime: Pick<DataSourceRuntimeManager, 'status'>;
  queryRuntime: Pick<QueryRuntimeManager, 'error'>;
  reloadDataSources: () => Promise<void>;
  openNodeEditor: () => void;
  closeNodeEditor: () => void;
}

function stateDefaultFor(type: RuntimeStateSchema[string]['type']): RuntimeStateSchema[string]['default'] {
  if (type === 'bool') return false;
  if (type === 'number') return 0;
  if (type === 'string') return '';
  return Array(Number(type.slice(-1))).fill(0) as RuntimeStateSchema[string]['default'];
}

/** Keeps graph-port metadata derived from the canonical Runtime State declaration. */
export function applyRuntimeSchemaToGraphs(
  schema: RuntimeStateSchema,
  dataGraphs: Record<string, DataGraphDef>,
  eventGraphs: Record<string, EventGraphDef>,
): void {
  for (const graph of Object.values(dataGraphs)) {
    const changedTypes = new Set<string>();
    for (const node of graph.nodes) {
      if (node.type !== 'State') continue;
      const declaration = schema[String(node.data.name ?? '')];
      if (!declaration) continue;
      if (node.data.valueType !== declaration.type) changedTypes.add(node.id);
      node.data.valueType = declaration.type;
      node.data.scope = declaration.scope;
    }
    if (changedTypes.size) {
      // A source's output type changed. Keeping its old typed wires would turn a
      // valid graph into a visually connected but semantically invalid one.
      graph.edges = graph.edges.filter(edge => !changedTypes.has(edge.fromNode));
    }
  }
  for (const graph of Object.values(eventGraphs)) {
    const changedSources = new Set<string>();
    const changedSetValues = new Set<string>();
    for (const node of graph.nodes) {
      if (node.type !== 'State' && node.type !== 'Set') continue;
      const declaration = schema[String(node.data.state ?? '')];
      if (!declaration) continue;
      if (node.data.valueType !== declaration.type) {
        if (node.type === 'State') changedSources.add(node.id);
        else changedSetValues.add(node.id);
      }
      node.data.valueType = declaration.type;
    }
    if (changedSources.size || changedSetValues.size) {
      graph.edges = graph.edges.filter(edge => (
        !changedSources.has(edge.fromNode)
        && !(changedSetValues.has(edge.toNode) && edge.toPort === 1)
      ));
    }
  }
}

function commitRuntimeModel(
  deps: LeftPanelDeps,
  schema: RuntimeStateSchema,
  dataGraphs = structuredClone(deps.appState.dataGraphs),
  eventGraphs = structuredClone(deps.appState.eventGraphs),
): void {
  applyRuntimeSchemaToGraphs(schema, dataGraphs, eventGraphs);
  deps.commands.setRuntimeModel(schema, dataGraphs, eventGraphs);
  deps.nodeEditor.renderGraph();
  deps.scheduleRender();
}

export function removeStateDependents<T extends DataGraphDef | EventGraphDef>(
  graphs: Record<string, T>,
  stateName: string,
  isDataGraph: boolean,
): Map<string, Set<string>> {
  const removedOutputs = new Map<string, Set<string>>();
  for (const [graphId, graph] of Object.entries(graphs)) {
    const removed = new Set(graph.nodes
      .filter(node => (
        (node.type === 'State' && node.data.name === stateName)
        || (!isDataGraph && (node.type === 'Set' || node.type === 'Toggle' || node.type === 'Add') && node.data.state === stateName)
      ))
      .map(node => node.id));
    if (removed.size === 0) continue;
    // A dependent graph node is never left connected to a deleted state command/source.
    // The cascade follows output and flow edges, preserving an executable graph after undoable deletion.
    let changed = true;
    while (changed) {
      changed = false;
      for (const edge of graph.edges) {
        if (removed.has(edge.fromNode) && !removed.has(edge.toNode)) {
          removed.add(edge.toNode);
          changed = true;
        }
      }
    }
    if (isDataGraph) {
      const outputs = new Set(graph.nodes
        .filter(node => removed.has(node.id) && node.type === 'Output')
        .map(node => String(node.data.inputName || 'Output')));
      if (outputs.size) removedOutputs.set(graphId, outputs);
    }
    graph.nodes = graph.nodes.filter(node => !removed.has(node.id));
    graph.edges = graph.edges.filter(edge => !removed.has(edge.fromNode) && !removed.has(edge.toNode));
  }
  return removedOutputs;
}

function removeStateAndDependentBindings(deps: LeftPanelDeps, stateName: string): void {
  const schema = structuredClone(deps.appState.runtimeState);
  if (!(stateName in schema)) return;
  delete schema[stateName];
  const dataGraphs = structuredClone(deps.appState.dataGraphs);
  const eventGraphs = structuredClone(deps.appState.eventGraphs);
  applyRuntimeSchemaToGraphs(schema, dataGraphs, eventGraphs);
  const removedOutputs = removeStateDependents(dataGraphs, stateName, true);
  removeStateDependents(eventGraphs, stateName, false);

  const clearRemovedBindings = (owner: { type: 'document' } | { type: 'scene'; nodeId: string }, instance: { use: string; bindings: Record<string, string> } | null | undefined): void => {
    if (!instance) return;
    const outputs = removedOutputs.get(instance.use);
    if (!outputs) return;
    const bindings = Object.fromEntries(Object.entries(instance.bindings).filter(([output]) => !outputs.has(output)));
    if (Object.keys(bindings).length !== Object.keys(instance.bindings).length) {
      deps.commands.setDataOwner(owner, { ...instance, bindings });
    }
  };

  deps.commandBatch.run(`Delete Runtime State: ${stateName}`, () => {
    deps.commands.setRuntimeModel(schema, dataGraphs, eventGraphs);
    clearRemovedBindings({ type: 'document' }, deps.appState.data);
    const visitedComponents = new Set<string>();
    const visit = (scene: AppState['scene']): void => {
      for (const node of Object.values(scene.nodes)) {
        clearRemovedBindings({ type: 'scene', nodeId: node.id }, node.behavior);
        if (node.kind !== 'instance' || visitedComponents.has(node.componentId)) continue;
        visitedComponents.add(node.componentId);
        const component = deps.appState.componentLibrary[node.componentId];
        if (component) visit(component.scene);
      }
    };
    visit(deps.appState.scene);
  });
}

export interface LeftPanelController {
  openTab(tabName: string): void;
  renderFavorites(): void;
  renderInspector(host: HTMLElement): boolean;
}

const COMMANDS: Array<{ label: string; keys: string }> = [
  { label: 'Undo action', keys: 'Ctrl+Z' },
  { label: 'Redo action', keys: 'Ctrl+Y / Ctrl+Shift+Z' },
  { label: 'Delete graph node', keys: 'Node editor focus + Delete' },
  { label: 'Cancel graph connection or cut', keys: 'Node editor focus + Esc' },
  { label: 'Cut connections', keys: 'Ctrl + drag' },
  { label: 'Pan canvas', keys: 'LMB drag / MMB' },
  { label: 'Zoom', keys: 'Wheel / Ctrl+Wheel' },
  { label: 'Add node', keys: 'RMB' }
];

const RUNTIME_GRAPH_COPY: Record<RuntimeGraphKind, {
  icon: string;
  graph: string;
  library: string;
  create: string;
  empty: string;
  namePlaceholder: string;
}> = {
  data: {
    icon: '⌘',
    graph: 'data graph',
    library: 'Data library',
    create: 'New data graph',
    empty: 'Select a data graph or data source.',
    namePlaceholder: 'button_behavior',
  },
  event: {
    icon: '↯',
    graph: 'event graph',
    library: 'Event library',
    create: 'New event graph',
    empty: 'Select an event graph or runtime state.',
    namePlaceholder: 'activate_button',
  },
  query: {
    icon: '⌕',
    graph: 'query graph',
    library: 'Query library',
    create: 'New query graph',
    empty: 'Select a query graph.',
    namePlaceholder: 'visible_items',
  },
};

function relativeTime(timestamp: number): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function renderShortcuts(): void {
  const container = document.getElementById('left-panel-shortcuts');
  if (!container) return;
  container.innerHTML = '';
  for (const cmd of COMMANDS) {
    const row = document.createElement('div');
    row.className = 'command-item';
    const label = document.createElement('span');
    label.textContent = cmd.label;
    const keys = document.createElement('span');
    keys.className = 'command-keys';
    keys.textContent = cmd.keys;
    row.appendChild(label);
    row.appendChild(keys);
    container.appendChild(row);
  }
}

function renderLibraryPanel(kind: 'geometry' | 'material', deps: LeftPanelDeps): void {
  const container = document.getElementById(kind === 'geometry' ? 'left-panel-geometry' : 'left-panel-materials');
  if (!container) return;
  container.innerHTML = '';
  const appState = deps.appState;

  const sections = [
    { title: 'Built-in', items: kind === 'geometry' ? appState.geometryLibrary.builtIn : appState.materialLibrary.builtIn },
    { title: 'User', items: kind === 'geometry' ? appState.geometryLibrary.user : appState.materialLibrary.user },
  ];

  for (const section of sections) {
    // The defaults are ordinary library entries — list them too (hiding the
    // default made Themed Color vanish the moment it became the default).
    const entries = Object.entries(section.items);
    if (entries.length === 0) continue;

    const title = document.createElement('div');
    title.className = 'library-panel-title';
    title.textContent = section.title;
    container.appendChild(title);

    for (const [id, item] of entries) {
      const row = document.createElement('button');
      row.className = 'history-item library-panel-item';
      row.type = 'button';
      if (appState.editorContext.mode === `${kind}-library` && appState.editorContext.libraryItemId === id) {
        row.classList.add('current');
      }
      row.addEventListener('click', () => {
        deps.editorNavigation.openLibraryEditor(kind, id);
        renderLibraryPanel(kind, deps);
      });

      const main = document.createElement('div');
      main.className = 'history-main';
      const label = document.createElement('div');
      label.className = 'history-label';
      label.textContent = item.name;
      main.appendChild(label);

      const meta = document.createElement('div');
      meta.className = 'history-time';
      meta.textContent = item.builtIn ? 'Built-in' : 'User';

      row.appendChild(main);
      row.appendChild(meta);
      container.appendChild(row);
    }
  }

  if (kind === 'geometry') renderShapesLibraryRows(deps, container);

  if (container.childElementCount === 0) {
    const empty = document.createElement('div');
    empty.className = 'left-panel-empty';
    empty.textContent = kind === 'geometry'
      ? 'Base geometry primitives appear here.'
      : 'Base material primitives appear here.';
    container.appendChild(empty);
  }
}

function renderShapesLibraryRows(deps: LeftPanelDeps, container: HTMLElement): void {
  const title = document.createElement('div');
  title.className = 'library-panel-title';
  title.textContent = 'Shapes';
  container.appendChild(title);

  const libraryRow = document.createElement('button');
  libraryRow.type = 'button';
  libraryRow.className = 'history-item library-panel-item';
  if (deps.appState.inspectorTarget.type === 'shape-library') libraryRow.classList.add('current');
  const libraryMain = document.createElement('div'); libraryMain.className = 'history-main';
  const libraryLabel = document.createElement('div'); libraryLabel.className = 'history-label'; libraryLabel.textContent = 'Shapes library';
  const libraryMeta = document.createElement('div'); libraryMeta.className = 'history-time'; libraryMeta.textContent = 'Library';
  libraryMain.appendChild(libraryLabel); libraryRow.append(libraryMain, libraryMeta);
  libraryRow.addEventListener('click', () => {
    deps.closeNodeEditor();
    deps.editorNavigation.returnToScene();
    deps.appState.inspectorTarget = { type: 'shape-library' };
    deps.renderSceneTree();
    renderLibraryPanel('geometry', deps);
  });
  container.appendChild(libraryRow);

  const groups: Array<{ label: string; builtIn: boolean; shapes: Record<string, SvgPathShape> }> = [
    { label: 'Built-in', builtIn: true, shapes: deps.appState.builtInShapes },
    { label: 'User', builtIn: false, shapes: deps.appState.shapes },
  ];
  for (const group of groups) {
    for (const [shapeId, shape] of Object.entries(group.shapes).sort((a, b) => a[1].name.localeCompare(b[1].name))) {
      const row = document.createElement('button'); row.type = 'button'; row.className = 'history-item library-panel-item';
      const target = deps.appState.inspectorTarget;
      if (target.type === 'shape' && target.shapeId === shapeId && target.builtIn === group.builtIn) row.classList.add('current');
      const main = document.createElement('div'); main.className = 'history-main';
      const label = document.createElement('div'); label.className = 'history-label'; label.textContent = shape.name || shapeId; main.appendChild(label);
      const meta = document.createElement('div'); meta.className = 'history-time'; meta.textContent = group.label;
      row.append(main, meta);
      row.addEventListener('click', () => {
        deps.closeNodeEditor();
        deps.editorNavigation.returnToScene();
        deps.appState.inspectorTarget = { type: 'shape', shapeId, builtIn: group.builtIn };
        deps.renderSceneTree();
        renderLibraryPanel('geometry', deps);
      });
      container.appendChild(row);
    }
  }
}

function firstVisibleLibraryItemId(kind: 'geometry' | 'material', appState: AppState): string | null {
  const groups = [
    kind === 'geometry' ? appState.geometryLibrary.builtIn : appState.materialLibrary.builtIn,
    kind === 'geometry' ? appState.geometryLibrary.user : appState.materialLibrary.user,
  ];

  for (const group of groups) {
    for (const id of Object.keys(group)) {
      return id;
    }
  }

  return null;
}

function openFirstLibraryItem(kind: 'geometry' | 'material', deps: LeftPanelDeps): void {
  const appState = deps.appState;
  const currentMatches = appState.editorContext.mode === `${kind}-library`
    && appState.editorContext.libraryItemId
    && (kind === 'geometry'
      ? (appState.geometryLibrary.builtIn[appState.editorContext.libraryItemId] ?? appState.geometryLibrary.user[appState.editorContext.libraryItemId])
      : (appState.materialLibrary.builtIn[appState.editorContext.libraryItemId] ?? appState.materialLibrary.user[appState.editorContext.libraryItemId]));
  if (currentMatches && appState.editorContext.libraryItemId) {
    deps.editorNavigation.openLibraryEditor(kind, appState.editorContext.libraryItemId);
    return;
  }

  const firstId = firstVisibleLibraryItemId(kind, appState);
  if (firstId) deps.editorNavigation.openLibraryEditor(kind, firstId);
}

function openSceneTarget(deps: LeftPanelDeps): void {
  deps.editorNavigation.returnToScene();
}

function isLibraryEditorActive(appState: AppState): boolean {
  return appState.editorContext.mode === 'geometry-library'
    || appState.editorContext.mode === 'material-library';
}

function isHistoryPanelVisible(panel: HTMLElement): boolean {
  return !panel.classList.contains('collapsed')
    && Boolean(panel.querySelector('.left-panel-tab[data-tab="history"].active'));
}

function renderLeftPanelHistory(deps: LeftPanelDeps): void {
  const container = document.getElementById('left-panel-history');
  if (!container) return;
  container.innerHTML = '';
  const historyStore = deps.historyStore;
  const history = historyStore.history;

  if (history.entries.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'history-item empty';
    empty.textContent = 'History is empty';
    container.appendChild(empty);
    return;
  }

  for (let i = history.entries.length - 1; i >= 0; i--) {
    const entry = history.entries[i];
    const row = document.createElement('div');
    row.className = 'history-item';
    if (i === history.currentIndex) row.classList.add('current');
    row.title = new Date(entry.timestamp).toLocaleString();

    const main = document.createElement('div');
    main.className = 'history-main';
    const label = document.createElement('div');
    label.className = 'history-label';
    label.textContent = entry.label;
    main.appendChild(label);

    const time = document.createElement('div');
    time.className = 'history-time';
    time.textContent = relativeTime(entry.timestamp);

    row.appendChild(main);
    row.appendChild(time);
    row.addEventListener('click', () => {
      if (!historyStore.jumpToHistory(i)) return;
      deps.nodeEditor.renderGraph();
      deps.updateParams();
      deps.renderSceneTree();
      deps.editorNavigation.render();
      deps.scheduleRender();
      renderLeftPanelHistory(deps);
    });
    container.appendChild(row);
  }
}

function renderLibraryPanels(deps: LeftPanelDeps): void {
  renderLibraryPanel('geometry', deps);
  renderLibraryPanel('material', deps);
  renderDataGraphPanel(deps);
}

function graphLibraryFor(deps: LeftPanelDeps, kind: RuntimeGraphKind): Record<string, DataGraphDef | EventGraphDef> {
  if (kind === 'data') return deps.appState.dataGraphs;
  if (kind === 'event') return deps.appState.eventGraphs;
  return deps.appState.queryGraphs;
}

function loadGraph(deps: LeftPanelDeps, kind: RuntimeGraphKind, graphId: string): boolean {
  return deps.loadRuntimeGraph(kind, graphId);
}

function setGraph(deps: LeftPanelDeps, kind: RuntimeGraphKind, graphId: string, graph: DataGraphDef | EventGraphDef | null): void {
  deps.commands.setRuntimeGraph(kind, graphId, graph);
}

function createDefaultGraph(kind: RuntimeGraphKind): DataGraphDef | EventGraphDef {
  if (kind === 'event') {
    return {
      nodes: [{ id: 'on_activate', type: 'OnEvent', x: 80, y: 80, data: { name: 'activate', fields: { sourceNodeId: 'string' } } }],
      edges: [],
    };
  }
  if (kind === 'query') {
    return {
      nodes: [
        { id: 'source', type: 'Source', x: 80, y: 80, data: { source: '' } },
        { id: 'output', type: 'Output', x: 360, y: 80, data: { inputName: 'result', inputType: 'collection', displayType: 'collection' } },
      ],
      edges: [{ fromNode: 'source', fromPort: 0, toNode: 'output', toPort: 0 }],
    };
  }
  return {
    nodes: [{
      id: 'output',
      type: 'Output',
      x: 320,
      y: 80,
      data: { inputName: 'Output', inputType: 'float', displayType: 'float' },
    }],
    edges: [],
  };
}

function createGraph(deps: LeftPanelDeps, kind: RuntimeGraphKind, graphId: string): void {
  setGraph(deps, kind, graphId, createDefaultGraph(kind));
  deps.appState.inspectorTarget = { type: 'graph', kind, graphId };
  loadGraph(deps, kind, graphId);
  deps.nodeEditor.renderGraph();
  deps.openNodeEditor();
  if (kind === 'event') renderEventGraphPanel(deps);
  else renderDataGraphPanel(deps);
  deps.renderSceneTree();
}

function beginGraphCreation(deps: LeftPanelDeps, kind: RuntimeGraphKind): void {
  const current = deps.appState.inspectorTarget;
  const returnTarget = current.type === 'graph' && current.kind === kind
    ? current
    : { type: 'graph-library' as const, kind };
  deps.appState.inspectorTarget = { type: 'new-graph', kind, returnTarget };
  deps.renderSceneTree();
}

function createDataSource(deps: LeftPanelDeps): void {
  let index = Object.keys(deps.appState.dataSources).length + 1;
  while (deps.appState.dataSources[`source_${index}`]) index++;
  const sourceId = `source_${index}`;
  deps.commands.setDataSources({
    ...deps.appState.dataSources,
    [sourceId]: { provider: 'json', resource: `${sourceId}.json`, records: structuredClone(ROOT_DATA_RECORDS), keyType: 'string', fields: {} },
  });
  deps.appState.inspectorTarget = { type: 'data-source', sourceId };
  renderDataGraphPanel(deps);
  deps.renderSceneTree();
}

function renderGraphLibraryRows(deps: LeftPanelDeps, kind: RuntimeGraphKind, container: HTMLElement): void {
  for (const graphId of Object.keys(graphLibraryFor(deps, kind)).sort()) {
    const row = document.createElement('div');
    row.className = 'history-item library-panel-item library-entity-item';
    row.role = 'button';
    row.tabIndex = 0;
    const target = deps.appState.inspectorTarget;
    if (target.type === 'graph' && target.kind === kind && target.graphId === graphId) row.classList.add('current');
    const main = document.createElement('div'); main.className = 'history-main';
    const label = document.createElement('div'); label.className = 'history-label'; label.textContent = graphId; main.appendChild(label);
    const openGraph = (): void => {
      if (!loadGraph(deps, kind, graphId)) return;
      deps.appState.inspectorTarget = { type: 'graph', kind, graphId };
      container.querySelectorAll('.library-panel-item.current').forEach(item => item.classList.remove('current'));
      row.classList.add('current');
      deps.nodeEditor.renderGraph();
      deps.openNodeEditor();
      deps.editorNavigation.render();
      deps.scheduleRender();
      deps.renderSceneTree();
    };
    row.addEventListener('click', openGraph);
    row.addEventListener('keydown', event => {
      if (event.target !== row || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      openGraph();
    });
    row.append(main);
    container.appendChild(row);
  }
}

function renderDataGraphPanel(deps: LeftPanelDeps): void {
  const container = document.getElementById('left-panel-data');
  if (!container) return;
  container.innerHTML = '';

  const title = document.createElement('div');
  title.className = 'library-panel-title';
  title.textContent = 'User';
  container.appendChild(title);

  renderGraphLibraryRows(deps, 'data', container);

  const queryTitle = document.createElement('div');
  queryTitle.className = 'library-panel-title';
  queryTitle.textContent = 'Queries';
  container.appendChild(queryTitle);
  renderGraphLibraryRows(deps, 'query', container);

  renderDataSourcesLibrary(deps, container);
}

function dataSourceReferenceCount(appState: AppState, sourceId: string): number {
  const graphReferences = Object.values(appState.dataGraphs)
    .flatMap(graph => graph.nodes)
    .filter(node => node.type === 'Lookup' && node.data.source === sourceId)
    .length;
  const scenes = [appState.scene, ...Object.values(appState.componentLibrary).map(component => component.scene)];
  const sceneReferences = scenes.reduce((count, scene) => (
    count + Object.values(scene.nodes).filter(node => node.dataContext?.source === sourceId).length
  ), 0);
  return graphReferences + sceneReferences;
}

function dataFieldReferenceCount(appState: AppState, sourceId: string, field: string): number {
  return Object.values(appState.dataGraphs)
    .flatMap(graph => graph.nodes)
    .filter(node => (node.type === 'Lookup' && node.data.source === sourceId && node.data.field === field)
      || (node.type === 'RecordField' && node.data.field === field))
    .length;
}

function renderDataSourcesLibrary(deps: LeftPanelDeps, container: HTMLElement): void {
  const title = document.createElement('div');
  title.className = 'library-panel-title';
  title.textContent = 'Data Sources';
  container.appendChild(title);

  for (const id of Object.keys(deps.appState.dataSources).sort()) {
    const row = document.createElement('div');
    row.className = 'history-item library-panel-item library-entity-item';
    row.role = 'button';
    row.tabIndex = 0;
    if (deps.appState.inspectorTarget.type === 'data-source' && deps.appState.inspectorTarget.sourceId === id) row.classList.add('current');
    const label = document.createElement('div');
    label.className = 'history-label';
    label.textContent = id;
    const select = (): void => {
      deps.appState.inspectorTarget = { type: 'data-source', sourceId: id };
      renderDataGraphPanel(deps);
      deps.renderSceneTree();
    };
    row.addEventListener('click', select);
    row.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      select();
    });
    row.appendChild(label);
    container.appendChild(row);
  }
}

function renderDataSourceInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  const id = deps.appState.inspectorTarget.type === 'data-source'
    ? deps.appState.inspectorTarget.sourceId
    : null;
  const source = id ? deps.appState.dataSources[id] : undefined;
  if (!id || !source) return false;
  container.innerHTML = '';
  const update = (id: string, next: DataSourceDef): void => {
    deps.commands.setDataSources({ ...deps.appState.dataSources, [id]: next });
    deps.nodeEditor.renderGraph();
    renderDataGraphPanel(deps);
    deps.renderSceneTree();
  };
    const loaded = deps.dataRuntime.getDataSources()[id];
    const sourceDocument = deps.dataRuntime.getSourceDocument(id);
    const discovered = discoverSourceFields(sourceDocument, source, id);
    const card = document.createElement('section');
    card.className = 'scene-props data-source-editor data-source-inspector';
    const header = document.createElement('div'); header.className = 'scene-props-header';
    const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = '▤';
    const name = document.createElement('span'); name.textContent = id;
    const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = 'data source';
    header.append(icon, name, kind);
    const actions = document.createElement('div'); actions.className = 'data-source-actions';
    const refresh = document.createElement('button'); refresh.type = 'button'; refresh.className = 'ui-btn ui-btn--control data-source-refresh'; refresh.textContent = 'Refresh';
    refresh.title = `Reload ${source.resource}`;
    refresh.addEventListener('click', async () => {
      refresh.disabled = true;
      refresh.textContent = 'Loading…';
      try {
        await deps.reloadDataSources();
      } catch (error) {
        refresh.title = error instanceof Error ? error.message : String(error);
      } finally {
        deps.nodeEditor.renderGraph();
        renderDataGraphPanel(deps);
        deps.renderSceneTree();
      }
    });
    const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'ui-btn ui-btn--control ui-btn--danger data-source-delete'; remove.textContent = 'Delete';
    const references = dataSourceReferenceCount(deps.appState, id);
    remove.disabled = references > 0;
    remove.title = references ? `Used by ${references} Lookup node${references === 1 ? '' : 's'}` : `Delete ${id}`;
    remove.addEventListener('click', () => {
      const sources = { ...deps.appState.dataSources }; delete sources[id]; deps.commands.setDataSources(sources);
      deps.appState.inspectorTarget = { type: 'scene' };
      renderDataGraphPanel(deps);
      deps.nodeEditor.renderGraph();
      deps.renderSceneTree();
    });
    const add = document.createElement('button'); add.type = 'button'; add.className = 'ui-btn ui-btn--control'; add.textContent = 'Add source';
    add.addEventListener('click', () => createDataSource(deps));
    actions.append(refresh, add, remove);
    const config = document.createElement('div'); config.className = 'data-source-config';
    const addSourceRow = (label: string, control: HTMLElement): void => {
      const row = document.createElement('div'); row.className = 'scene-prop-row';
      const key = document.createElement('label'); key.className = 'scene-prop-label'; key.textContent = label;
      row.append(key, control); config.appendChild(row);
    };
    addSourceRow('Provider', createProviderSelect(source.provider, value => update(id, { ...source, provider: value })));
    addSourceRow('Resource', createSourceInput(source.resource, value => update(id, { ...source, resource: value })));
    addSourceRow('Credential', createSourceInput(source.credential ?? '', value => update(id, { ...source, credential: value || undefined })));
    addSourceRow('Key type', createSourceTypeSelect(source.keyType, value => {
      if (value === source.keyType) return;
      const nextGraphs = structuredClone(deps.appState.dataGraphs);
      const contextNodes = [deps.appState.scene, ...Object.values(deps.appState.componentLibrary).map(component => component.scene)]
        .flatMap(scene => Object.values(scene.nodes))
        .filter(node => node.dataContext?.source === id);
      for (const graph of Object.values(nextGraphs)) for (const node of graph.nodes) {
        if (node.type !== 'Lookup' || node.data.source !== id) continue;
        node.data.keyType = value;
        graph.edges = graph.edges.filter(edge => !(edge.toNode === node.id && edge.toPort === 0));
      }
      deps.commandBatch.run(`Set Data Source Key Type: ${id}`, () => {
        deps.commands.setDataSources({ ...deps.appState.dataSources, [id]: { ...source, keyType: value } });
        for (const node of contextNodes) {
          const current = node.dataContext!.key;
          const key = value === 'number'
            ? (Number.isFinite(Number(current)) ? Number(current) : 0)
            : value === 'bool'
              ? current === true || current === 'true'
              : String(current);
          deps.commands.setSceneDataContext(node.id, { source: id, key });
        }
        for (const [graphId, graph] of Object.entries(nextGraphs)) {
          if (JSON.stringify(graph) !== JSON.stringify(deps.appState.dataGraphs[graphId])) deps.commands.setRuntimeGraph('data', graphId, graph);
        }
      });
      renderDataGraphPanel(deps);
      deps.nodeEditor.renderGraph();
    }));
    addSourceRow('Records', createRecordsPathSelect(source.records, discoverRecordCollections(sourceDocument), next => {
      const keyType = next.kind === 'array' ? 'number' : source.keyType;
      update(id, { ...source, records: next, keyType });
    }));
    addSourceRow('Kind', createRecordsKindSelect(source.records.kind, next => {
      if (next === source.records.kind) return;
      update(id, { ...source, records: { ...source.records, kind: next }, keyType: next === 'array' ? 'number' : source.keyType });
    }));
    const status = document.createElement('div'); status.className = 'scene-prop-row data-source-status';
    const statusLabel = document.createElement('div'); statusLabel.className = 'scene-prop-label'; statusLabel.textContent = 'Status';
    const statusValue = document.createElement('div'); statusValue.className = 'scene-prop-input scene-prop-input--readonly';
    const runtimeStatus = deps.dataSourceRuntime.status(id);
    const count = loaded && typeof loaded === 'object' ? Object.keys(loaded).length : 0;
    statusValue.textContent = runtimeStatus.status === 'ready'
      ? `${count} records · r${runtimeStatus.revision} · schema ${runtimeStatus.schemaRevision}`
      : runtimeStatus.error ? `${runtimeStatus.status}: ${runtimeStatus.error}` : runtimeStatus.status;
    status.append(statusLabel, statusValue);
    const fields = document.createElement('div'); fields.className = 'data-source-fields';
    const fieldsLabel = document.createElement('div'); fieldsLabel.className = 'scene-props-section'; fieldsLabel.textContent = 'Fields';
    fields.appendChild(fieldsLabel);
    for (const [fieldId, field] of Object.entries(source.fields)) {
      const fieldRow = document.createElement('div'); fieldRow.className = 'scene-prop-row data-source-field';
      const fieldName = createDataFieldPathSelect(field, discovered, next => {
        if (dataFieldPathKey(next.path) === dataFieldPathKey(field.path)) return;
        const nextFields = { ...source.fields, [fieldId]: next };
        const nextGraphs = structuredClone(deps.appState.dataGraphs);
        for (const graph of Object.values(nextGraphs)) for (const node of graph.nodes) {
          if (!((node.type === 'Lookup' && node.data.source === id) || node.type === 'RecordField') || node.data.field !== fieldId) continue;
          if (node.data.valueType !== next.type) {
            node.data.valueType = next.type;
            graph.edges = graph.edges.filter(edge => !(edge.fromNode === node.id && edge.fromPort === 0));
          }
        }
        deps.commandBatch.run(`Set Data Field Path: ${fieldId}`, () => {
          deps.commands.setDataSources({ ...deps.appState.dataSources, [id]: { ...source, fields: nextFields } });
          for (const [graphId, graph] of Object.entries(nextGraphs)) {
            if (JSON.stringify(graph) !== JSON.stringify(deps.appState.dataGraphs[graphId])) deps.commands.setRuntimeGraph('data', graphId, graph);
          }
        });
        renderDataGraphPanel(deps);
        deps.nodeEditor.renderGraph();
      });
      const fieldType = createDataFieldTypeSelect(field.type, next => {
        if (next === field.type) return;
        const nextGraphs = structuredClone(deps.appState.dataGraphs);
        for (const graph of Object.values(nextGraphs)) for (const node of graph.nodes) {
          if (!((node.type === 'Lookup' && node.data.source === id) || node.type === 'RecordField') || node.data.field !== fieldId) continue;
          node.data.valueType = next;
          graph.edges = graph.edges.filter(edge => !(edge.fromNode === node.id && edge.fromPort === 0));
        }
        deps.commandBatch.run(`Set Data Field Type: ${fieldId}`, () => {
          deps.commands.setDataSources({ ...deps.appState.dataSources, [id]: { ...source, fields: { ...source.fields, [fieldId]: { ...field, type: next } } } });
          for (const [graphId, graph] of Object.entries(nextGraphs)) {
            if (JSON.stringify(graph) !== JSON.stringify(deps.appState.dataGraphs[graphId])) deps.commands.setRuntimeGraph('data', graphId, graph);
          }
        });
        renderDataGraphPanel(deps);
        deps.nodeEditor.renderGraph();
      });
      const removeField = document.createElement('button'); removeField.type = 'button'; removeField.className = 'ui-btn ui-btn--compact ui-btn--danger data-source-field-delete'; removeField.textContent = 'Delete';
      const references = dataFieldReferenceCount(deps.appState, id, fieldId);
      removeField.disabled = references > 0 || Object.keys(source.fields).length === 1;
      removeField.title = references ? `Used by ${references} Lookup node${references === 1 ? '' : 's'}` : 'A source needs at least one field';
      removeField.addEventListener('click', () => {
        const nextFields = { ...source.fields }; delete nextFields[fieldId]; update(id, { ...source, fields: nextFields });
      });
      const fieldLabel = document.createElement('div'); fieldLabel.className = 'scene-prop-label'; fieldLabel.textContent = 'Field';
      const fieldControls = document.createElement('div'); fieldControls.className = 'data-source-field-controls';
      fieldControls.append(fieldName, fieldType, removeField);
      fieldRow.append(fieldLabel, fieldControls); fields.appendChild(fieldRow);
    }
    const addField = document.createElement('button'); addField.type = 'button'; addField.className = 'ui-btn ui-btn--compact data-source-add'; addField.textContent = '+ Add loaded field';
    addField.addEventListener('click', () => {
      const selectedPaths = new Set(Object.values(source.fields).map(field => dataFieldPathKey(field.path)));
      const next = discovered.find(field => !selectedPaths.has(dataFieldPathKey(field.path)));
      if (!next) return;
      const fieldId = uniqueDataFieldId(next.path, source.fields);
      update(id, { ...source, fields: { ...source.fields, [fieldId]: next } });
    });
    const selectedPaths = new Set(Object.values(source.fields).map(field => dataFieldPathKey(field.path)));
    addField.disabled = discovered.every(field => selectedPaths.has(dataFieldPathKey(field.path)));
    if (discovered.length) fields.appendChild(addField);
    else {
      const empty = document.createElement('div'); empty.className = 'data-source-empty';
      empty.textContent = 'Refresh to inspect fields from the source file.';
      fields.appendChild(empty);
    }
    const actionsSection = document.createElement('div'); actionsSection.className = 'scene-props-section'; actionsSection.textContent = 'Actions';
    card.append(header, config, status, fields, actionsSection, actions);
    container.appendChild(card);
    return true;
}

function renderLibraryInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  const target = deps.appState.inspectorTarget;
  if (target.type !== 'graph-library') return false;
  const isDataArea = target.kind === 'data' || target.kind === 'query';
  const copy = RUNTIME_GRAPH_COPY[target.kind];
  container.innerHTML = '';
  const panel = document.createElement('section'); panel.className = 'scene-props graph-library-inspector';
  const header = document.createElement('div'); header.className = 'scene-props-header';
  const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = copy.icon;
  const title = document.createElement('span'); title.textContent = copy.library;
  const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = 'library';
  header.append(icon, title, kind); panel.appendChild(header);
  const message = document.createElement('div'); message.className = 'right-inspector-empty';
  message.textContent = copy.empty;
  panel.appendChild(message);
  const section = document.createElement('div'); section.className = 'scene-props-section'; section.textContent = 'Actions'; panel.appendChild(section);
  const row = document.createElement('div'); row.className = 'scene-prop-row scene-prop-row-actions';
  const spacer = document.createElement('div'); spacer.className = 'scene-prop-label-spacer scene-prop-label'; spacer.textContent = 'Actions';
  const actions = document.createElement('div'); actions.className = 'scene-prop-action-group';
  const graph = document.createElement('button'); graph.type = 'button'; graph.className = 'ui-btn ui-btn--control'; graph.textContent = copy.create;
  graph.addEventListener('click', () => beginGraphCreation(deps, target.kind));
  actions.appendChild(graph);
  if (isDataArea) {
    const source = document.createElement('button'); source.type = 'button'; source.className = 'ui-btn ui-btn--control'; source.textContent = 'Add source';
    source.addEventListener('click', () => createDataSource(deps));
    actions.appendChild(source);
    if (target.kind === 'data') {
      const query = document.createElement('button'); query.type = 'button'; query.className = 'ui-btn ui-btn--control'; query.textContent = 'New query graph';
      query.addEventListener('click', () => beginGraphCreation(deps, 'query'));
      actions.insertBefore(query, source);
    }
  } else {
    const state = document.createElement('button'); state.type = 'button'; state.className = 'ui-btn ui-btn--control'; state.textContent = 'New state';
    state.addEventListener('click', () => createRuntimeState(deps));
    actions.appendChild(state);
  }
  row.append(spacer, actions); panel.appendChild(row); container.appendChild(panel);
  return true;
}

interface SvgPathInfo {
  viewBox: [number, number, number, number];
  contourCount: number;
  arcCount: number;
}

function inspectSvgPath(d: string): { info?: SvgPathInfo; error?: string } {
  try {
    return { info: svgPathInfoWasm(d) as SvgPathInfo };
  } catch (error) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
}

function shapeReferences(appState: AppState, shapeId: string): string[] {
  const references: string[] = [];
  const scan = (nodes: readonly import('../graph/nodeDefs.js').GraphNode[], owner: string): void => {
    for (const node of nodes) {
      if (node.type === 'Shape' && node.data.shapeRef === shapeId) references.push(`${owner} / ${node.id}`);
    }
  };
  for (const [id, graph] of Object.entries(appState.geometryLibrary.builtIn)) scan(graph.nodes, `Built-in geometry ${id}`);
  for (const [id, graph] of Object.entries(appState.geometryLibrary.user)) scan(graph.nodes, `Geometry ${id}`);
  for (const [id, layer] of Object.entries(appState.layerLibrary)) {
    if (layer.geometry.type === 'inline') scan(layer.geometry.nodes, `Layer ${id}`);
  }
  return references;
}

function uniqueShapeId(appState: AppState, name: string): string {
  const base = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'shape';
  const used = new Set([...Object.keys(appState.builtInShapes), ...Object.keys(appState.shapes)]);
  if (!used.has(base)) return base;
  for (let suffix = 2; ; suffix++) {
    const candidate = `${base}_${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
}

function beginShapeCreation(deps: LeftPanelDeps): void {
  const current = deps.appState.inspectorTarget;
  const returnTarget = current.type === 'shape' || current.type === 'shape-library'
    ? current
    : { type: 'shape-library' as const };
  deps.appState.inspectorTarget = { type: 'new-shape', returnTarget };
  deps.renderSceneTree();
}

function renderShapeLibraryInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  if (deps.appState.inspectorTarget.type !== 'shape-library') return false;
  container.innerHTML = '';
  const panel = document.createElement('section'); panel.className = 'scene-props graph-library-inspector';
  const header = document.createElement('div'); header.className = 'scene-props-header';
  const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = '◇';
  const title = document.createElement('span'); title.textContent = 'Shapes library';
  const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = 'Library';
  header.append(icon, title, kind); panel.appendChild(header);
  const message = document.createElement('div'); message.className = 'right-inspector-empty';
  message.textContent = 'Reusable SVG path assets referenced by Shape nodes.'; panel.appendChild(message);
  const section = document.createElement('div'); section.className = 'scene-props-section'; section.textContent = 'Actions'; panel.appendChild(section);
  const row = document.createElement('div'); row.className = 'scene-prop-row scene-prop-row-actions';
  const spacer = document.createElement('div'); spacer.className = 'scene-prop-label-spacer scene-prop-label'; spacer.textContent = 'Actions';
  const actions = document.createElement('div'); actions.className = 'scene-prop-action-group';
  const create = document.createElement('button'); create.type = 'button'; create.className = 'ui-btn ui-btn--control'; create.textContent = 'New shape';
  create.addEventListener('click', () => beginShapeCreation(deps)); actions.appendChild(create);
  row.append(spacer, actions); panel.appendChild(row); container.appendChild(panel);
  return true;
}

function renderShapeForm(
  deps: LeftPanelDeps,
  container: HTMLElement,
  options: { title: string; shape?: SvgPathShape; shapeId?: string; builtIn?: boolean; create?: boolean },
): void {
  container.innerHTML = '';
  const original = options.shape;
  const panel = document.createElement('section'); panel.className = 'scene-props graph-library-inspector';
  const header = document.createElement('div'); header.className = 'scene-props-header';
  const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = '◇';
  const title = document.createElement('span'); title.textContent = options.title;
  const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = options.builtIn ? 'Built-in shape' : 'Shape';
  header.append(icon, title, kind); panel.appendChild(header);
  const details = document.createElement('div'); details.className = 'scene-props-section'; details.textContent = 'Details'; panel.appendChild(details);

  const field = (labelText: string, control: HTMLElement): void => {
    const row = document.createElement('div'); row.className = 'scene-prop-row';
    const label = document.createElement('label'); label.className = 'scene-prop-label'; label.textContent = labelText;
    row.append(label, control); panel.appendChild(row);
  };
  const name = document.createElement('input'); name.className = 'scene-prop-input'; name.value = original?.name ?? ''; name.placeholder = 'Shape name';
  field('Name', name);
  if (options.shapeId) {
    const id = document.createElement('div'); id.className = 'scene-prop-input scene-prop-input--readonly'; id.textContent = options.shapeId; id.title = options.shapeId; field('ID', id);
  }
  const d = document.createElement('textarea'); d.className = 'scene-prop-input scene-prop-textarea'; d.rows = 6; d.value = original?.d ?? ''; d.placeholder = 'M … Z'; field('Path data', d);
  const viewBox = document.createElement('div'); viewBox.className = 'scene-prop-vector shape-viewbox-inputs';
  const viewInputs = [0, 1, 2, 3].map(index => {
    const labels = ['X', 'Y', 'Width', 'Height'];
    const input = document.createElement('input'); input.className = 'scene-prop-input'; input.type = 'number'; input.step = 'any'; input.value = String(original?.viewBox[index] ?? (index < 2 ? 0 : 24)); input.title = `ViewBox ${labels[index]}`; input.setAttribute('aria-label', `ViewBox ${labels[index]}`); viewBox.appendChild(input); return input;
  });
  field('ViewBox', viewBox);
  const fillRule = document.createElement('select'); fillRule.className = 'scene-prop-input';
  fillRule.append(new Option('Non-zero', 'nonzero'), new Option('Even-odd', 'evenodd'));
  fillRule.value = original?.fillRule ?? 'nonzero'; field('Fill rule', fillRule);
  const status = document.createElement('div'); status.className = 'data-binding-empty'; panel.appendChild(status);

  const actionsLabel = document.createElement('div'); actionsLabel.className = 'scene-props-section'; actionsLabel.textContent = 'Actions'; panel.appendChild(actionsLabel);
  const actionRow = document.createElement('div'); actionRow.className = 'scene-prop-row scene-prop-row-actions';
  const spacer = document.createElement('div'); spacer.className = 'scene-prop-label-spacer scene-prop-label'; spacer.textContent = 'Actions';
  const actions = document.createElement('div'); actions.className = 'scene-prop-action-group shape-action-group';
  const fit = document.createElement('button'); fit.type = 'button'; fit.className = 'ui-btn ui-btn--control'; fit.textContent = 'Fit viewBox to path';
  const save = document.createElement('button'); save.type = 'button'; save.className = 'ui-btn ui-btn--control'; save.textContent = options.create ? 'Create' : 'Apply';
  const duplicate = document.createElement('button'); duplicate.type = 'button'; duplicate.className = 'ui-btn ui-btn--control'; duplicate.textContent = 'Duplicate';
  const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'ui-btn ui-btn--control ui-btn--danger'; remove.textContent = 'Delete';
  const cancel = document.createElement('button'); cancel.type = 'button'; cancel.className = 'ui-btn ui-btn--control'; cancel.textContent = 'Cancel';
  const references = options.shapeId ? shapeReferences(deps.appState, options.shapeId) : [];

  const candidate = (): SvgPathShape | null => {
    const values = viewInputs.map(input => Number(input.value));
    const inspection = inspectSvgPath(d.value);
    const valid = name.value.trim().length > 0
      && values.every(Number.isFinite) && values[2] > 0 && values[3] > 0 && Boolean(inspection.info);
    status.textContent = inspection.error
      ? inspection.error
      : inspection.info
        ? `${inspection.info.contourCount} contours · ${inspection.info.arcCount} fitted arcs`
        : 'Enter SVG path data.';
    status.classList.toggle('invalid', !valid);
    save.disabled = !valid || Boolean(options.builtIn);
    fit.disabled = !inspection.info || Boolean(options.builtIn);
    return valid ? { kind: 'svgPath', name: name.value.trim(), d: d.value.trim(), viewBox: values as [number, number, number, number], fillRule: fillRule.value as SvgPathShape['fillRule'] } : null;
  };
  for (const control of [name, d, fillRule, ...viewInputs]) control.addEventListener('input', candidate);
  fit.addEventListener('click', () => {
    const info = inspectSvgPath(d.value).info;
    if (!info) return;
    info.viewBox.forEach((value, index) => { viewInputs[index].value = String(value); });
    candidate();
  });
  save.addEventListener('click', () => {
    const shape = candidate(); if (!shape) return;
    const shapeId = options.shapeId ?? uniqueShapeId(deps.appState, shape.name);
    deps.commands.setShapes({ ...deps.appState.shapes, [shapeId]: shape });
    deps.appState.inspectorTarget = { type: 'shape', shapeId, builtIn: false };
    deps.scheduleRender(); deps.renderSceneTree(); renderLibraryPanel('geometry', deps);
  });
  duplicate.addEventListener('click', () => {
    if (!original) return;
    const shapeId = uniqueShapeId(deps.appState, `${original.name} copy`);
    deps.commands.setShapes({ ...deps.appState.shapes, [shapeId]: { ...structuredClone(original), name: `${original.name} copy` } });
    deps.appState.inspectorTarget = { type: 'shape', shapeId, builtIn: false };
    deps.scheduleRender(); deps.renderSceneTree(); renderLibraryPanel('geometry', deps);
  });
  remove.disabled = Boolean(options.builtIn) || references.length > 0;
  remove.title = references.length ? `Used by ${references.join(', ')}` : 'Delete shape';
  remove.addEventListener('click', () => {
    if (!options.shapeId || remove.disabled) return;
    const shapes = { ...deps.appState.shapes }; delete shapes[options.shapeId]; deps.commands.setShapes(shapes);
    deps.appState.inspectorTarget = { type: 'shape-library' };
    deps.scheduleRender(); deps.renderSceneTree(); renderLibraryPanel('geometry', deps);
  });
  cancel.addEventListener('click', () => {
    const target = deps.appState.inspectorTarget;
    deps.appState.inspectorTarget = target.type === 'new-shape' ? target.returnTarget : { type: 'shape-library' };
    deps.renderSceneTree();
  });
  if (options.create) actions.append(save, fit, cancel);
  else if (options.builtIn) actions.append(duplicate);
  else actions.append(save, fit, duplicate, remove);
  actionRow.append(spacer, actions); panel.appendChild(actionRow); container.appendChild(panel); candidate();
}

function renderShapeInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  const target = deps.appState.inspectorTarget;
  if (target.type === 'new-shape') {
    renderShapeForm(deps, container, { title: 'New shape', create: true });
    return true;
  }
  if (target.type !== 'shape') return false;
  const shape = (target.builtIn ? deps.appState.builtInShapes : deps.appState.shapes)[target.shapeId];
  if (!shape) return false;
  renderShapeForm(deps, container, { title: shape.name || target.shapeId, shape, shapeId: target.shapeId, builtIn: target.builtIn });
  return true;
}

function renderEmptyPanelInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  const target = deps.appState.inspectorTarget;
  if (target.type !== 'panel') return false;
  const labels: Record<EmptyInspectorPanel, string> = {
    actions: 'Actions',
    favorites: 'Favorites',
    geometry: 'Geometry library',
    materials: 'Material library',
    history: 'History',
    hotkeys: 'Hotkeys',
    settings: 'Settings',
  };
  container.innerHTML = '';
  const panel = document.createElement('section'); panel.className = 'scene-props graph-library-inspector';
  const header = document.createElement('div'); header.className = 'scene-props-header';
  const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = '·';
  const title = document.createElement('span'); title.textContent = labels[target.panel];
  const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = 'Inspector';
  header.append(icon, title, kind); panel.appendChild(header);
  const empty = document.createElement('div'); empty.className = 'right-inspector-empty';
  empty.textContent = 'No inspector controls for this panel yet.';
  panel.appendChild(empty);
  container.appendChild(panel);
  return true;
}

function renderGraphInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  const target = deps.appState.inspectorTarget;
  if (target.type !== 'graph') return false;
  const isData = target.kind === 'data';
  const copy = RUNTIME_GRAPH_COPY[target.kind];
  const id = target.graphId;
  const graph = graphLibraryFor(deps, target.kind)[id];
  if (!graph) return false;

  container.innerHTML = '';
  const panel = document.createElement('section');
  panel.className = 'scene-props graph-library-inspector';
  const header = document.createElement('div'); header.className = 'scene-props-header';
  const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = copy.icon;
  const title = document.createElement('span'); title.textContent = id;
  const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = copy.graph;
  header.append(icon, title, kind);
  panel.appendChild(header);

  const details = document.createElement('div'); details.className = 'scene-props-section'; details.textContent = 'Details'; panel.appendChild(details);
  const addInfo = (label: string, value: string): void => {
    const row = document.createElement('div'); row.className = 'scene-prop-row';
    const key = document.createElement('div'); key.className = 'scene-prop-label'; key.textContent = label;
    const content = document.createElement('div'); content.className = 'scene-prop-input scene-prop-input--readonly'; content.textContent = value; content.title = value;
    row.append(key, content); panel.appendChild(row);
  };
  addInfo('Nodes', String(graph.nodes.length));
  addInfo('Connections', String(graph.edges.length));
  if (target.kind === 'query') addInfo('Runtime', deps.queryRuntime.error(id) ?? 'Ready');
  const references = isData
    ? dataGraphReferenceCount(deps.appState, id)
    : target.kind === 'event' ? eventGraphReferenceCount(deps.appState, id) : queryGraphReferenceCount(deps.appState, id);
  addInfo('Used by', references === 0 ? 'Not used' : `${references} ${references === 1 ? 'binding' : 'bindings'}`);

  const actionsLabel = document.createElement('div'); actionsLabel.className = 'scene-props-section'; actionsLabel.textContent = 'Actions'; panel.appendChild(actionsLabel);
  const actionRow = document.createElement('div'); actionRow.className = 'scene-prop-row scene-prop-row-actions';
  const spacer = document.createElement('div'); spacer.className = 'scene-prop-label-spacer scene-prop-label'; spacer.textContent = 'Actions';
  const actionGroup = document.createElement('div'); actionGroup.className = 'scene-prop-action-group';
  const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'ui-btn ui-btn--control ui-btn--danger'; remove.textContent = 'Delete graph';
  remove.disabled = references > 0;
  remove.title = references > 0 ? 'Remove graph bindings before deleting it' : `Delete ${id}`;
  remove.addEventListener('click', () => {
    if (remove.disabled) return;
    setGraph(deps, target.kind, id, null);
    deps.appState.inspectorTarget = { type: 'scene' };
    deps.editorNavigation.returnToScene();
    renderDataGraphPanel(deps);
    renderEventGraphPanel(deps);
  });
  const create = document.createElement('button'); create.type = 'button'; create.className = 'ui-btn ui-btn--control'; create.textContent = copy.create;
  create.addEventListener('click', () => beginGraphCreation(deps, target.kind));
  actionGroup.append(create, remove); actionRow.append(spacer, actionGroup); panel.appendChild(actionRow);
  container.appendChild(panel);
  return true;
}

function renderNewGraphInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  const target = deps.appState.inspectorTarget;
  if (target.type !== 'new-graph') return false;
  const copy = RUNTIME_GRAPH_COPY[target.kind];
  container.innerHTML = '';
  const panel = document.createElement('section'); panel.className = 'scene-props graph-library-inspector';
  const header = document.createElement('div'); header.className = 'scene-props-header';
  const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = copy.icon;
  const title = document.createElement('span'); title.textContent = copy.create;
  const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = copy.graph;
  header.append(icon, title, kind); panel.appendChild(header);
  const section = document.createElement('div'); section.className = 'scene-props-section'; section.textContent = 'Details'; panel.appendChild(section);
  const row = document.createElement('div'); row.className = 'scene-prop-row';
  const label = document.createElement('label'); label.className = 'scene-prop-label'; label.textContent = 'Name';
  const name = document.createElement('input'); name.className = 'scene-prop-input'; name.placeholder = copy.namePlaceholder; name.autocomplete = 'off';
  row.append(label, name); panel.appendChild(row);
  const hint = document.createElement('div'); hint.className = 'data-binding-empty'; hint.textContent = 'Use a unique graph name.'; panel.appendChild(hint);
  const actionsLabel = document.createElement('div'); actionsLabel.className = 'scene-props-section'; actionsLabel.textContent = 'Actions'; panel.appendChild(actionsLabel);
  const actionRow = document.createElement('div'); actionRow.className = 'scene-prop-row scene-prop-row-actions';
  const spacer = document.createElement('div'); spacer.className = 'scene-prop-label-spacer scene-prop-label'; spacer.textContent = 'Actions';
  const actions = document.createElement('div'); actions.className = 'scene-prop-action-group';
  const create = document.createElement('button'); create.type = 'button'; create.className = 'ui-btn ui-btn--control'; create.textContent = 'Create';
  const cancel = document.createElement('button'); cancel.type = 'button'; cancel.className = 'ui-btn ui-btn--control'; cancel.textContent = 'Cancel';
  const sync = (): void => {
    const graphId = name.value.trim();
    const valid = graphId.length > 0 && !Object.hasOwn(graphLibraryFor(deps, target.kind), graphId);
    create.disabled = !valid;
    hint.textContent = !graphId ? 'Enter a graph name.' : valid ? 'The name is available.' : 'A graph with this name already exists.';
  };
  name.addEventListener('input', sync);
  name.addEventListener('keydown', event => { if (event.key === 'Enter' && !create.disabled) create.click(); });
  create.addEventListener('click', () => {
    const graphId = name.value.trim();
    const graphs = graphLibraryFor(deps, target.kind);
    if (!graphId || Object.hasOwn(graphs, graphId)) return;
    createGraph(deps, target.kind, graphId);
  });
  cancel.addEventListener('click', () => { deps.appState.inspectorTarget = target.returnTarget; deps.renderSceneTree(); });
  actions.append(create, cancel); actionRow.append(spacer, actions); panel.appendChild(actionRow); container.appendChild(panel);
  sync(); name.focus();
  return true;
}

function discoverSourceFields(document: unknown, source: DataSourceDef, sourceId: string): DataFieldDef[] {
  if (document === undefined) return [];
  try {
    return discoverDataFields(resolveDataRecords(document, source.records.path, sourceId));
  } catch {
    return [];
  }
}

function discoverRecordCollections(document: unknown): DataRecordsDef[] {
  const found: DataRecordsDef[] = [];
  const visit = (value: unknown, path: DataRecordsDef['path'], depth: number): void => {
    if (!value || typeof value !== 'object' || depth > 5) return;
    found.push({ path, kind: Array.isArray(value) ? 'array' : 'map' });
    if (Array.isArray(value)) {
      value.forEach((child, index) => visit(child, [...path, index], depth + 1));
    } else {
      for (const [key, child] of Object.entries(value as Record<string, unknown>)) visit(child, [...path, key], depth + 1);
    }
  };
  visit(document, [], 0);
  return found;
}

function recordsPathLabel(path: DataRecordsDef['path']): string {
  return path.length === 0 ? 'Root' : ['Root', ...path.map(segment => String(segment))].join(' › ');
}

function createRecordsPathSelect(value: DataRecordsDef, choices: DataRecordsDef[], commit: (value: DataRecordsDef) => void): HTMLSelectElement {
  const select = document.createElement('select'); select.className = 'node-value-input';
  const recordsKey = (records: DataRecordsDef): string => JSON.stringify([records.path, records.kind]);
  const options = choices.length ? choices : [value];
  for (const choice of options) {
    const option = new Option(`${recordsPathLabel(choice.path)} · ${choice.kind === 'array' ? 'Array' : 'Map'}`, JSON.stringify(choice));
    select.appendChild(option);
  }
  const current = recordsKey(value);
  if (!options.some(choice => recordsKey(choice) === current)) {
    select.appendChild(new Option(`${recordsPathLabel(value.path)} · ${value.kind === 'array' ? 'Array' : 'Map'}`, JSON.stringify(value)));
  }
  select.value = Array.from(select.options).find(option => recordsKey(JSON.parse(option.value) as DataRecordsDef) === current)?.value ?? '';
  select.addEventListener('change', () => commit(JSON.parse(select.value) as DataRecordsDef));
  return select;
}

function createRecordsKindSelect(value: DataRecordsDef['kind'], commit: (value: DataRecordsDef['kind']) => void): HTMLSelectElement {
  const select = document.createElement('select'); select.className = 'node-value-input';
  for (const kind of ['array', 'map'] as const) select.appendChild(new Option(kind === 'array' ? 'Array' : 'Map', kind));
  select.value = value; select.addEventListener('change', () => commit(select.value as DataRecordsDef['kind'])); return select;
}

function createSourceInput(value: string, commit: (value: string) => void): HTMLInputElement {
  const input = document.createElement('input'); input.className = 'node-value-input'; input.value = value; input.addEventListener('change', () => commit(input.value.trim())); return input;
}

function createProviderSelect(value: string, commit: (value: string) => void): HTMLSelectElement {
  const select = document.createElement('select'); select.className = 'node-value-input';
  for (const provider of ['json', 'memory', 'rest', 'websocket']) select.appendChild(new Option(provider, provider));
  if (!Array.from(select.options).some(option => option.value === value)) select.appendChild(new Option(value, value));
  select.value = value;
  select.addEventListener('change', () => commit(select.value));
  return select;
}

function createDataFieldPathSelect(
  value: DataFieldDef,
  discovered: DataFieldDef[],
  commit: (value: DataFieldDef) => void,
): HTMLSelectElement {
  const select = document.createElement('select'); select.className = 'node-value-input';
  const currentKey = dataFieldPathKey(value.path);
  for (const field of discovered) select.appendChild(new Option(dataFieldPathLabel(field.path), dataFieldPathKey(field.path)));
  if (!discovered.some(field => dataFieldPathKey(field.path) === currentKey)) {
    select.appendChild(new Option(dataFieldPathLabel(value.path), currentKey));
  }
  select.value = currentKey;
  select.addEventListener('change', () => {
    const selected = discovered.find(field => dataFieldPathKey(field.path) === select.value);
    if (selected) commit(selected);
  });
  return select;
}

function createSourceTypeSelect(value: DataSourceDef['keyType'], commit: (value: DataSourceDef['keyType']) => void): HTMLSelectElement {
  const select = document.createElement('select'); select.className = 'node-value-input';
  for (const type of ['string', 'number', 'bool'] as const) select.appendChild(new Option(type, type));
  select.value = value; select.addEventListener('change', () => commit(select.value as DataSourceDef['keyType'])); return select;
}

function createDataFieldTypeSelect(value: DataFieldDef['type'], commit: (value: DataFieldDef['type']) => void): HTMLSelectElement {
  const select = document.createElement('select'); select.className = 'node-value-input';
  for (const type of ['string', 'number', 'bool', 'vec2', 'vec3', 'vec4'] as const) select.appendChild(new Option(type, type));
  select.value = value; select.addEventListener('change', () => commit(select.value as DataFieldDef['type'])); return select;
}

function createRuntimeState(deps: LeftPanelDeps): void {
  let index = Object.keys(deps.appState.runtimeState).length + 1;
  while (deps.appState.runtimeState[`state_${index}`]) index++;
  const stateName = `state_${index}`;
  commitRuntimeModel(deps, {
    ...deps.appState.runtimeState,
    [stateName]: { scope: 'global', type: 'number', default: 0 },
  });
  deps.appState.inspectorTarget = { type: 'runtime-state', stateName };
  renderEventGraphPanel(deps);
  deps.renderSceneTree();
}

function renderRuntimeStateInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  const target = deps.appState.inspectorTarget;
  if (target.type !== 'runtime-state') return false;
  const stateName = target.stateName;
  const definition = deps.appState.runtimeState[stateName];
  if (!definition) return false;

  container.innerHTML = '';
  const panel = document.createElement('section'); panel.className = 'scene-props runtime-state-inspector';
  const header = document.createElement('div'); header.className = 'scene-props-header';
  const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = '◉';
  const title = document.createElement('span'); title.textContent = stateName;
  const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = 'runtime state';
  header.append(icon, title, kind); panel.appendChild(header);
  const section = document.createElement('div'); section.className = 'scene-props-section'; section.textContent = 'State'; panel.appendChild(section);

  const apply = (name: string, next: RuntimeStateSchema[string]): void => {
    if (!name || (name !== stateName && deps.appState.runtimeState[name])) return;
    const schema = structuredClone(deps.appState.runtimeState);
    delete schema[stateName]; schema[name] = next;
    const dataGraphs = structuredClone(deps.appState.dataGraphs);
    const eventGraphs = structuredClone(deps.appState.eventGraphs);
    if (name !== stateName) {
      for (const graph of Object.values(dataGraphs)) for (const node of graph.nodes) if (node.type === 'State' && node.data.name === stateName) node.data.name = name;
      for (const graph of Object.values(eventGraphs)) for (const node of graph.nodes) {
        if (node.type === 'State' && node.data.state === stateName) node.data.state = name;
        if ((node.type === 'Set' || node.type === 'Toggle' || node.type === 'Add') && node.data.state === stateName) node.data.state = name;
      }
    }
    commitRuntimeModel(deps, schema, dataGraphs, eventGraphs);
    deps.appState.inspectorTarget = { type: 'runtime-state', stateName: name };
    renderEventGraphPanel(deps); deps.renderSceneTree();
  };
  const addRow = (label: string, control: HTMLElement): void => {
    const row = document.createElement('div'); row.className = 'scene-prop-row';
    const key = document.createElement('label'); key.className = 'scene-prop-label'; key.textContent = label;
    row.append(key, control); panel.appendChild(row);
  };
  const name = document.createElement('input'); name.className = 'scene-prop-input'; name.value = stateName;
  name.addEventListener('change', () => { const next = name.value.trim(); if (!next || (next !== stateName && deps.appState.runtimeState[next])) { name.value = stateName; return; } apply(next, definition); });
  addRow('Name', name);
  const scope = document.createElement('select'); scope.className = 'scene-prop-input';
  for (const value of ['global', 'owner'] as const) scope.appendChild(new Option(value === 'global' ? 'Global' : 'Owner', value));
  scope.value = definition.scope;
  scope.addEventListener('change', () => apply(stateName, { ...definition, scope: scope.value as RuntimeStateSchema[string]['scope'] }));
  addRow('Scope', scope);
  const type = document.createElement('select'); type.className = 'scene-prop-input';
  for (const value of ['bool', 'number', 'string', 'vec2', 'vec3', 'vec4'] as const) type.appendChild(new Option(value, value));
  type.value = definition.type;
  type.addEventListener('change', () => { const next = type.value as RuntimeStateSchema[string]['type']; apply(stateName, { ...definition, type: next, default: stateDefaultFor(next) }); });
  addRow('Type', type);
  const defaultValue = document.createElement('input'); defaultValue.className = 'scene-prop-input'; defaultValue.value = typeof definition.default === 'string' ? definition.default : JSON.stringify(definition.default);
  defaultValue.addEventListener('change', () => {
    let value: RuntimeStateSchema[string]['default'];
    try {
      value = definition.type === 'string' ? defaultValue.value : JSON.parse(defaultValue.value);
    } catch { defaultValue.value = typeof definition.default === 'string' ? definition.default : JSON.stringify(definition.default); return; }
    const valid = definition.type === 'bool' ? typeof value === 'boolean'
      : definition.type === 'number' ? typeof value === 'number'
      : definition.type === 'string' ? typeof value === 'string'
      : Array.isArray(value) && value.length === Number(definition.type.slice(-1)) && value.every(item => typeof item === 'number');
    if (!valid) { defaultValue.value = typeof definition.default === 'string' ? definition.default : JSON.stringify(definition.default); return; }
    apply(stateName, { ...definition, default: value });
  });
  addRow('Default', defaultValue);
  const actionsLabel = document.createElement('div'); actionsLabel.className = 'scene-props-section'; actionsLabel.textContent = 'Actions'; panel.appendChild(actionsLabel);
  const actions = document.createElement('div'); actions.className = 'scene-prop-row scene-prop-row-actions';
  const spacer = document.createElement('div'); spacer.className = 'scene-prop-label-spacer scene-prop-label'; spacer.textContent = 'Actions';
  const group = document.createElement('div'); group.className = 'scene-prop-action-group';
  const add = document.createElement('button'); add.type = 'button'; add.className = 'ui-btn ui-btn--control'; add.textContent = 'New state'; add.addEventListener('click', () => createRuntimeState(deps));
  const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'ui-btn ui-btn--control ui-btn--danger'; remove.textContent = 'Delete state';
  remove.addEventListener('click', () => { removeStateAndDependentBindings(deps, stateName); deps.appState.inspectorTarget = { type: 'graph-library', kind: 'event' }; deps.nodeEditor.renderGraph(); deps.scheduleRender(); renderEventGraphPanel(deps); deps.renderSceneTree(); });
  group.append(add, remove); actions.append(spacer, group); panel.appendChild(actions);
  container.appendChild(panel);
  return true;
}

function renderRuntimeInspector(deps: LeftPanelDeps, container: HTMLElement): boolean {
  if (deps.appState.inspectorTarget.type !== 'runtime') return false;
  container.innerHTML = '';
  const panel = document.createElement('section'); panel.className = 'scene-props runtime-session-inspector';
  const header = document.createElement('div'); header.className = 'scene-props-header';
  const icon = document.createElement('span'); icon.className = 'scene-props-header-icon'; icon.textContent = '◷';
  const title = document.createElement('span'); title.textContent = 'Runtime';
  const kind = document.createElement('span'); kind.className = 'scene-props-header-kind'; kind.textContent = 'session';
  header.append(icon, title, kind); panel.appendChild(header);
  const section = document.createElement('div'); section.className = 'scene-props-section'; section.textContent = 'Current values'; panel.appendChild(section);
  const addRow = (label: string, value: string): void => {
    const row = document.createElement('div'); row.className = 'scene-prop-row';
    const key = document.createElement('div'); key.className = 'scene-prop-label'; key.textContent = label;
    const content = document.createElement('div'); content.className = 'scene-prop-input scene-prop-input--readonly'; content.textContent = value; content.title = value;
    row.append(key, content); panel.appendChild(row);
  };
  try {
    const snapshot = deps.eventRuntime.inspectorSnapshot();
    const global = Object.entries(snapshot.global);
    if (global.length === 0) addRow('Global', 'No declared values');
    else for (const [key, value] of global) { const text = String(value); addRow(key, text.length > 64 ? `${text.slice(0, 61)}…` : text); }
    if (deps.eventRuntime.lastChangedKeys.length) addRow('Changed', deps.eventRuntime.lastChangedKeys.join(', '));
    if (deps.eventRuntime.lastError) addRow('Error', deps.eventRuntime.lastError);
  } catch { addRow('Status', 'Runtime is not initialized.'); }
  const actionsLabel = document.createElement('div'); actionsLabel.className = 'scene-props-section'; actionsLabel.textContent = 'Actions'; panel.appendChild(actionsLabel);
  const row = document.createElement('div'); row.className = 'scene-prop-row scene-prop-row-actions';
  const spacer = document.createElement('div'); spacer.className = 'scene-prop-label-spacer scene-prop-label'; spacer.textContent = 'Actions';
  const actions = document.createElement('div'); actions.className = 'scene-prop-action-group';
  const reset = document.createElement('button'); reset.type = 'button'; reset.className = 'ui-btn ui-btn--control'; reset.textContent = 'Reset'; reset.addEventListener('click', () => { deps.eventRuntime.syncSchema(deps.appState.runtimeState); deps.eventRuntime.reset(); renderEventGraphPanel(deps); deps.renderSceneTree(); deps.scheduleRender(); });
  const exportSnapshot = document.createElement('button'); exportSnapshot.type = 'button'; exportSnapshot.className = 'ui-btn ui-btn--control'; exportSnapshot.textContent = 'Export state'; exportSnapshot.addEventListener('click', () => { const blob = new Blob([JSON.stringify(deps.eventRuntime.exportSnapshot(), null, 2)], { type: 'application/json' }); const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = 'fluveria-runtime-state.json'; link.click(); URL.revokeObjectURL(link.href); });
  const importSnapshot = document.createElement('button'); importSnapshot.type = 'button'; importSnapshot.className = 'ui-btn ui-btn--control'; importSnapshot.textContent = 'Import state'; importSnapshot.addEventListener('click', () => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'application/json,.json'; input.addEventListener('change', async () => { const file = input.files?.[0]; if (!file) return; try { deps.eventRuntime.restoreSnapshot(JSON.parse(await file.text())); deps.renderSceneTree(); deps.scheduleRender(); } catch (error) { alert(error instanceof Error ? error.message : 'Invalid runtime snapshot'); } }); input.click(); });
  actions.append(reset, exportSnapshot, importSnapshot); row.append(spacer, actions); panel.appendChild(row); container.appendChild(panel);
  return true;
}

function renderEventGraphPanel(deps: LeftPanelDeps): void {
  const container = document.getElementById('left-panel-events');
  if (!container) return;
  container.innerHTML = '';
  const stateTitle = document.createElement('div'); stateTitle.className = 'library-panel-title'; stateTitle.textContent = 'Runtime State'; container.appendChild(stateTitle);
  for (const [name, definition] of Object.entries(deps.appState.runtimeState)) {
    const row = document.createElement('div'); row.className = 'history-item library-panel-item'; row.role = 'button'; row.tabIndex = 0;
    if (deps.appState.inspectorTarget.type === 'runtime-state' && deps.appState.inspectorTarget.stateName === name) row.classList.add('current');
    const label = document.createElement('div'); label.className = 'history-label'; label.textContent = name;
    const meta = document.createElement('div'); meta.className = 'history-meta'; meta.textContent = `${definition.scope} · ${definition.type}`;
    const select = (): void => { deps.appState.inspectorTarget = { type: 'runtime-state', stateName: name }; deps.renderSceneTree(); renderEventGraphPanel(deps); };
    row.addEventListener('click', select);
    row.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); select(); } });
    row.append(label, meta); container.appendChild(row);
  }
  const runtimeTitle = document.createElement('div'); runtimeTitle.className = 'library-panel-title'; runtimeTitle.textContent = 'Runtime'; container.appendChild(runtimeTitle);
  const runtimeRow = document.createElement('button'); runtimeRow.type = 'button'; runtimeRow.className = 'history-item library-panel-item';
  if (deps.appState.inspectorTarget.type === 'runtime') runtimeRow.classList.add('current');
  const runtimeMain = document.createElement('div'); runtimeMain.className = 'history-main';
  const runtimeLabel = document.createElement('div'); runtimeLabel.className = 'history-label'; runtimeLabel.textContent = 'Runtime session';
  const runtimeMeta = document.createElement('div'); runtimeMeta.className = 'history-meta'; runtimeMeta.textContent = 'State values';
  runtimeMain.append(runtimeLabel, runtimeMeta); runtimeRow.appendChild(runtimeMain);
  runtimeRow.addEventListener('click', () => { deps.appState.inspectorTarget = { type: 'runtime' }; deps.renderSceneTree(); renderEventGraphPanel(deps); });
  container.appendChild(runtimeRow);
  const title = document.createElement('div'); title.className = 'library-panel-title'; title.textContent = 'User'; container.appendChild(title);
  renderGraphLibraryRows(deps, 'event', container);
}

function eventGraphReferenceCount(appState: AppState, graphId: string): number {
  let count = 0;
  const visit = (scene: AppState['scene']): void => {
    for (const node of Object.values(scene.nodes)) {
      if (node.events?.activate?.graph === graphId) count++;
      if (node.kind === 'instance') { const component = appState.componentLibrary[node.componentId]; if (component) visit(component.scene); }
    }
  };
  visit(appState.scene); return count;
}

function dataGraphReferenceCount(appState: AppState, graphId: string): number {
  let count = appState.data?.use === graphId ? 1 : 0;
  const visitedComponents = new Set<string>();
  const visit = (scene: AppState['scene']): void => {
    for (const node of Object.values(scene.nodes)) {
      if (node.behavior?.use === graphId) count++;
      if (node.kind === 'instance') {
        if (visitedComponents.has(node.componentId)) continue;
        visitedComponents.add(node.componentId);
        const component = appState.componentLibrary[node.componentId];
        if (component) visit(component.scene);
      }
    }
  };
  visit(appState.scene);
  return count;
}

function queryGraphReferenceCount(appState: AppState, graphId: string): number {
  return Object.values(appState.dataGraphs)
    .flatMap(graph => graph.nodes)
    .filter(node => node.type === 'Query' && node.data.query === graphId)
    .length;
}

function renderLeftPanelFavorites(deps: LeftPanelDeps): void {
  const container = document.getElementById('left-panel-favorites');
  if (!container) return;
  container.innerHTML = '';

  if (deps.appState.sceneFavorites.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'history-item empty';
    empty.textContent = 'No favorites yet';
    container.appendChild(empty);
    return;
  }

  for (const favorite of deps.appState.sceneFavorites) {
    const valid = isSceneFavoriteTargetValid(deps.appState, favorite.target);
    const row = document.createElement('div');
    row.className = 'history-item favorite-item';
    if (!valid) row.classList.add('missing');

    const main = document.createElement('div');
    main.className = 'history-main';
    const label = document.createElement('div');
    label.className = 'history-label';
    label.textContent = getSceneFavoriteTargetLabel(deps.appState, favorite.target);
    main.appendChild(label);

    const meta = document.createElement('div');
    meta.className = 'history-time';
    meta.textContent = valid ? 'Favorite' : 'Missing';
    main.appendChild(meta);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'scene-tree-chip favorite-remove';
    remove.textContent = 'Remove';
    remove.addEventListener('click', e => {
      e.stopPropagation();
      if (!removeSceneFavorite(deps.appState, favorite.id)) return;
      deps.renderSceneTree();
      renderLeftPanelFavorites(deps);
    });

    row.appendChild(main);
    row.appendChild(remove);
    if (valid) {
      row.addEventListener('click', () => {
        deps.selectSceneFavorite(favorite.target);
        renderLeftPanelFavorites(deps);
      });
    }
    container.appendChild(row);
  }
}

function renderVisibleLeftPanelHistory(deps: LeftPanelDeps): void {
  const panel = document.getElementById('left-panel');
  if (!panel || !isHistoryPanelVisible(panel)) return;
  renderLeftPanelHistory(deps);
}

function switchTab(tabName: string, deps: LeftPanelDeps): void {
  const panel = document.getElementById('left-panel')!;
  const previousTab = panel.querySelector<HTMLElement>('.left-panel-tab.active[data-tab]')?.dataset.tab;
  if (previousTab && previousTab !== tabName) deps.closeNodeEditor();
  panel.querySelectorAll<HTMLElement>('.left-panel-tab[data-tab]').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.tab === tabName);
  });
  panel.querySelectorAll<HTMLElement>('.left-panel-content').forEach(content => {
    content.classList.toggle('active', content.dataset.panel === tabName);
  });

  // External Inspector targets belong only to Data/Event library workflows.
  // A human selecting any ordinary left panel must regain the scene Inspector;
  // otherwise a stale graph target can hijack Settings or the Scene Tree.
  if (tabName === 'scene') {
    deps.appState.inspectorTarget = { type: 'scene' };
  } else if (tabName !== 'data' && tabName !== 'events') {
    const emptyPanelTabs: EmptyInspectorPanel[] = ['actions', 'favorites', 'geometry', 'materials', 'history', 'hotkeys', 'settings'];
    if (emptyPanelTabs.includes(tabName as EmptyInspectorPanel)) {
      deps.appState.inspectorTarget = { type: 'panel', panel: tabName as EmptyInspectorPanel };
      deps.renderSceneTree();
    }
  }

  // Lazy-render content when switching
  if (tabName === 'scene') openSceneTarget(deps);
  if (tabName === 'geometry') openFirstLibraryItem('geometry', deps);
  if (tabName === 'materials') openFirstLibraryItem('material', deps);
  if (tabName === 'data') {
    deps.editorNavigation.returnToScene();
    deps.appState.inspectorTarget = { type: 'graph-library', kind: 'data' };
    deps.renderSceneTree();
  }
  if (tabName === 'events') {
    deps.editorNavigation.returnToScene();
    deps.appState.inspectorTarget = { type: 'graph-library', kind: 'event' };
    deps.renderSceneTree();
  }
  if (tabName === 'favorites') renderLeftPanelFavorites(deps);
  if (tabName === 'history') renderLeftPanelHistory(deps);
  if (tabName === 'hotkeys') renderShortcuts();
  if (tabName === 'geometry') renderLibraryPanel('geometry', deps);
  if (tabName === 'materials') renderLibraryPanel('material', deps);
  if (tabName === 'data') renderDataGraphPanel(deps);
  if (tabName === 'events') renderEventGraphPanel(deps);
}

export function initLeftPanel(deps: LeftPanelDeps): LeftPanelController {
  const controller: LeftPanelController = {
    openTab(tabName: string): void {
      const panel = document.getElementById('left-panel');
      if (!panel) return;
      panel.classList.remove('collapsed');
      switchTab(tabName, deps);
    },
    renderFavorites(): void {
      renderLeftPanelFavorites(deps);
    },
    renderInspector(host: HTMLElement): boolean {
      return renderDataSourceInspector(deps, host)
        || renderShapeInspector(deps, host)
        || renderShapeLibraryInspector(deps, host)
        || renderGraphInspector(deps, host)
        || renderNewGraphInspector(deps, host)
        || renderRuntimeStateInspector(deps, host)
        || renderRuntimeInspector(deps, host)
        || renderLibraryInspector(deps, host)
        || renderEmptyPanelInspector(deps, host);
    },
  };

  const panel = document.getElementById('left-panel');
  if (!panel) return controller;

  // Tab switching
  panel.querySelectorAll<HTMLElement>('.left-panel-tab[data-tab]').forEach(tab => {
    tab.addEventListener('click', () => {
      const name = tab.dataset.tab!;
      const shouldReturnToScene = name === 'scene' && isLibraryEditorActive(deps.appState);
      // If clicking the already-active tab, toggle panel collapse
      if (tab.classList.contains('active') && !panel.classList.contains('collapsed') && !shouldReturnToScene) {
        panel.classList.add('collapsed');
        return;
      }
      // Ensure panel is expanded
      panel.classList.remove('collapsed');
      switchTab(name, deps);
    });
  });

  // Collapse button
  const collapseBtn = document.getElementById('left-panel-collapse');
  if (collapseBtn) {
    collapseBtn.addEventListener('click', () => {
      panel.classList.toggle('collapsed');
      renderVisibleLeftPanelHistory(deps);
    });
  }

  // Prevent canvas interactions from firing when the user interacts with the panel.
  // These events would otherwise bubble up to #graph-area and trigger panning,
  // zooming, cut gestures, or the context menu.
  for (const type of ['mousedown', 'wheel', 'contextmenu'] as const) {
    panel.addEventListener(type, e => e.stopPropagation());
  }

  deps.historyStore.subscribeHistoryChanged(() => {
    renderVisibleLeftPanelHistory(deps);
    renderLibraryPanels(deps);
  });
  subscribeEditorTargetSynced(() => renderLibraryPanels(deps));
  window.setInterval(() => renderVisibleLeftPanelHistory(deps), 30000);

  // Initial render
  renderShortcuts();
  renderLibraryPanels(deps);
  openSceneTarget(deps);

  return controller;
}
